import React from 'react';
import {
    FaUsers,
    FaUser,
    FaPhone,
    FaEnvelope,
    FaMapMarkerAlt,
    FaExclamationTriangle,
    FaLock
} from 'react-icons/fa';
import env from '../../env';
import UserBadges from './UserBadges';
import './ContactAgentsOwner.css';

const ContactAgentsOwner = ({ agents, user, isPrivate, onContactClick }) => {
    const hasAgents = agents && agents.length > 0;
    const hasUser = user && !hasAgents;

    if (!hasAgents && !hasUser) {
        return null;
    }

    return (
        <div 
            className={`contact-agents-owner-card ${isPrivate ? 'private-auction-contact' : ''}`}
        >
            {/* Private Auction Warning Banner */}
            {isPrivate && (
                <div className="private-auction-banner">
                    <div className="banner-content">
                        <FaLock className="banner-icon" />
                        <div className="banner-text">
                            <strong>Private Auction - Invitation Required</strong>
                            <span>Contact below to request an invitation</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="contact-header">
                <div className="contact-icon-wrapper">
                    {hasAgents ? (
                        <FaUsers className="contact-icon" />
                    ) : (
                        <FaUser className="contact-icon" />
                    )}
                </div>
                <div className="contact-title-section">
                    <h5 className="contact-title">
                        {hasAgents ? 'Contact Agents' : 'Contact Owner'}
                    </h5>
                    <small className="contact-subtitle">
                        {hasAgents 
                            ? `${agents.length} ${agents.length === 1 ? 'agent' : 'agents'} available to assist you`
                            : 'Auction owner contact information'
                        }
                    </small>
                </div>
            </div>

            {/* Private Auction Invitation Message - Only once at the top */}
            {isPrivate && (
                <div className="invitation-message-top">
                    <FaExclamationTriangle className="invitation-icon" />
                    <div className="invitation-text">
                        <strong>Invitation Required</strong>
                        <span>Call or email to request an invitation to this private auction</span>
                    </div>
                </div>
            )}

            {/* Agents List or User Info */}
            <div className="contact-list">
                {hasAgents ? (
                    agents.map((agent) => (
                        <ContactCard
                            key={agent.id}
                            type="agent"
                            data={agent}
                            isPrivate={isPrivate}
                            onContactClick={onContactClick}
                        />
                    ))
                ) : hasUser ? (
                    <ContactCard
                        type="user"
                        data={user}
                        isPrivate={isPrivate}
                        onContactClick={onContactClick}
                    />
                ) : null}
            </div>
        </div>
    );
};

const ContactCard = ({ type, data, isPrivate, onContactClick }) => {
    const isAgent = type === 'agent';
    const name = isAgent ? data.agent_name : (data.full_name || `${data.first_name || ''} ${data.last_name || ''}`.trim() || 'Auction Owner');
    const photoUrl = isAgent ? data.photo_url : data.profile_image_url;
    const email = isAgent ? data.email : data.email;
    const phone = isAgent ? data.phone_whatsapp : data.phone_number;
    const designation = isAgent ? data.designation : data.bio;
    const location = isAgent ? null : data.location;
    const languages = isAgent ? data.languages_spoken : null;

    return (
        <div className={`contact-card ${isPrivate ? 'private-contact-card' : ''}`}>
            <div className="contact-card-content">
                <div className="contact-profile-section">
                    {/* Profile Picture */}
                    <div className="contact-avatar-wrapper">
                        {photoUrl ? (
                            <img
                                src={photoUrl.startsWith('http') 
                                    ? photoUrl 
                                    : env.BACKEND_URL + photoUrl}
                                alt={name}
                                className="contact-avatar"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    if (e.target.nextSibling) {
                                        e.target.nextSibling.style.display = 'flex';
                                    }
                                }}
                            />
                        ) : null}
                        <div
                            className="contact-avatar-placeholder"
                            style={{ display: photoUrl ? 'none' : 'flex' }}
                        >
                            {name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        {/* Online Status Indicator */}
                        <div className="online-indicator"></div>
                    </div>

                    {/* Name and Designation */}
                    <div className="contact-info">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <h6 className="contact-name">{name}</h6>
                            {/* Show badges if user has them */}
                            {data && (data.is_verified || data.seller_trust_badge || data.buyer_protection_plus) && (
                                <UserBadges user={data} iconSize={16} />
                            )}
                        </div>
                        {designation && (
                            <p className="contact-designation" style={{ color: '#64748b' }}>{designation}</p>
                        )}
                    </div>
                </div>

                {/* Quick Action Buttons */}
                <div className="contact-actions">
                    {phone && (
                        <a 
                            href={`tel:${phone}`}
                            className="contact-action-btn call-btn"
                            onClick={(e) => {
                                if (onContactClick) {
                                    onContactClick('call', phone);
                                }
                            }}
                        >
                            <FaPhone className="btn-icon" />
                            {isPrivate ? 'Call for Invitation' : 'Call'}
                        </a>
                    )}
                    {email && (
                        <a 
                            href={`mailto:${email}${isPrivate ? '?subject=Invitation Request for Private Auction' : ''}`}
                            className="contact-action-btn email-btn"
                            onClick={(e) => {
                                if (onContactClick) {
                                    onContactClick('email', email);
                                }
                            }}
                        >
                            <FaEnvelope className="btn-icon" />
                            {isPrivate ? 'Email for Invitation' : 'Email'}
                        </a>
                    )}
                </div>

                {/* Contact Details */}
                <div className="contact-details">
                    {email && (
                        <div className="contact-detail-item">
                            <div className="detail-icon email-icon">
                                <FaEnvelope />
                            </div>
                            <a 
                                href={`mailto:${email}${isPrivate ? '?subject=Invitation Request for Private Auction' : ''}`}
                                className="detail-link"
                            >
                                {email}
                            </a>
                        </div>
                    )}
                    {phone && (
                        <div className="contact-detail-item">
                            <div className="detail-icon phone-icon">
                                <FaPhone />
                            </div>
                            <a 
                                href={`tel:${phone}`}
                                className="detail-link"
                            >
                                {phone}
                            </a>
                        </div>
                    )}
                    {location && (
                        <div className="contact-detail-item">
                            <div className="detail-icon location-icon">
                                <FaMapMarkerAlt />
                            </div>
                            <span className="detail-text">{location}</span>
                        </div>
                    )}
                </div>

                {/* Languages (for agents only) */}
                {languages && languages.length > 0 && (
                    <div className="contact-languages">
                        <small className="languages-label">Languages Spoken</small>
                        <div className="languages-list">
                            {languages.map((lang, idx) => (
                                <span key={idx} className="language-badge">
                                    {lang}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContactAgentsOwner;

